import { useQuery } from '@atlassian/conversation-assistant-service/common/utils/use-query';
import { performGqlFetch } from '@atlassian/conversation-assistant-utils/graphqlUtils';

import { parseAriSafely } from '../../../../common/utils';

import { extractHostname } from './extractHostname';
import { isStagingEnvironment } from './isStagingEnvironment';
import { type ValidateImpactedWorkAriResult } from './useValidateImpactedWorkAri';

const TENANT_CONTEXTS_QUERY = `
	query useValidateJiraAlignProjectAriTenantContexts(
		$url: String!
		$currentCloudId: ID!
	) {
		byHost: tenantContexts(hostNames: [$url]) {
			orgId
		}
		byCloudId: tenantContexts(cloudIds: [$currentCloudId]) {
			orgId
		}
	}
`;

const IS_SANDBOX_QUERY = `
	query useValidateJiraAlignProjectAriIsSandbox($cloudId: ID!) {
		mercury @optIn(to: "Mercury") {
			workspaceContext(cloudId: $cloudId) {
				isSandbox
			}
		}
	}
`;

const JA_SITES_BY_ORG_QUERY = `
	query useValidateJiraAlignProjectAriJiraAlignSites($orgId: String!, $env: String!) {
		jiraAlignAgg_sitesByOrgId(orgId: $orgId, env: $env) @optIn(to: "JiraAlignAgg") {
			id
		}
	}
`;

type TenantContextsResponse = {
	byHost: { orgId: string }[];
	byCloudId: { orgId: string }[];
};

type IsSandboxResponse = {
	mercury: {
		workspaceContext: {
			isSandbox: boolean;
		};
	};
};

type JiraAlignSitesResponse = {
	jiraAlignAgg_sitesByOrgId: { id: string }[];
};

/**
 * Validates a Jira Align project ARI against the user's current Mercury workspace.
 *
 * Two checks are performed (mirroring the Mercury asks-page `useLinkResolverQuery.ts`):
 *   1. Org check — the resolved JA URL's host must belong to the same org as
 *      the current Mercury workspace.
 *   2. JA site check — the ARI's JA instance cloudId must appear in
 *      `jiraAlignAgg_sitesByOrgId(orgId, env)` for the current workspace's
 *      org.
 *
 * @param ari            - The raw ARI string. The hook parses it internally.
 * @param currentCloudId - The cloudId of the current Mercury workspace.
 * @param link           - The resolved URL of the impacted-work resource. Used
 *                         as the host-name lookup key for the org check.
 */
export const useValidateJiraAlignProjectAri = (
	ari: string | null,
	currentCloudId: string,
	link: string | null,
): ValidateImpactedWorkAriResult => {
	const parsed = ari ? parseAriSafely(ari) : null;
	const ariCloudId = parsed?.resourceOwner === 'jira-align' ? (parsed.cloudId ?? null) : null;

	const orgsEnabled = !!ariCloudId && !!currentCloudId && !!link;

	const {
		data: orgs,
		isFetching: orgsLoading,
		error: orgsError,
	} = useQuery({
		queryKey: ['focus-ask-impacted-work-validate-ja-project-orgs', currentCloudId, link ?? ''],
		enabled: orgsEnabled,
		queryFn: () =>
			performGqlFetch<TenantContextsResponse, { url: string; currentCloudId: string }>({
				gqlQuery: TENANT_CONTEXTS_QUERY,
				variables: { url: extractHostname(link), currentCloudId },
			}),
	});

	const {
		data: sandboxData,
		isFetching: sandboxLoading,
		error: sandboxError,
	} = useQuery({
		queryKey: ['focus-ask-impacted-work-validate-ja-project-is-sandbox', currentCloudId],
		enabled: orgsEnabled,
		queryFn: () =>
			performGqlFetch<IsSandboxResponse, { cloudId: string }>({
				gqlQuery: IS_SANDBOX_QUERY,
				variables: { cloudId: currentCloudId },
			}),
	});

	const workspaceOrg = orgs?.data?.byCloudId?.[0]?.orgId;
	const ariOrg = orgs?.data?.byHost?.[0]?.orgId;
	const isSandbox = sandboxData?.data?.mercury?.workspaceContext?.isSandbox;
	// Mirrors Mercury's logic: sandbox workspaces always use 'sandbox';
	// otherwise 'dev' on Atlassian staging deploys, 'production' elsewhere.
	const jaEnv = isSandbox === true ? 'sandbox' : isStagingEnvironment() ? 'dev' : 'production';

	const jaSitesEnabled =
		!!ariCloudId &&
		!!workspaceOrg &&
		!!ariOrg &&
		workspaceOrg === ariOrg &&
		isSandbox !== undefined;
	const {
		data: jaSites,
		isFetching: jaLoading,
		error: jaError,
	} = useQuery({
		queryKey: ['focus-ask-impacted-work-validate-ja-project-sites', workspaceOrg ?? '', jaEnv],
		enabled: jaSitesEnabled,
		queryFn: () =>
			performGqlFetch<JiraAlignSitesResponse, { orgId: string; env: string }>({
				gqlQuery: JA_SITES_BY_ORG_QUERY,
				variables: { orgId: workspaceOrg ?? '', env: jaEnv },
			}),
	});

	if (!ariCloudId) return { status: 'notFound' };
	if (orgsLoading || sandboxLoading || jaLoading) return { status: 'loading' };

	if (orgsEnabled && orgs === undefined && !orgsError) return { status: 'loading' }; // orgs enabled but not yet in flight
	if (orgsEnabled && sandboxData === undefined && !sandboxError) return { status: 'loading' }; // sandbox enabled but not yet in flight

	if (orgsEnabled && sandboxData != null && isSandbox === undefined && !sandboxError)
		return { status: 'notFound' }; // sandbox query resolved but workspaceContext was null (e.g. permission denied)

	if (jaSitesEnabled && jaSites === undefined && !jaError) return { status: 'loading' }; // jaSites enabled but not yet in flight

	if (orgsError || sandboxError || jaError) return { status: 'error' };

	if (!workspaceOrg || !ariOrg || ariOrg !== workspaceOrg) {
		return { status: 'notFound' };
	}

	const jaInstanceCloudIds =
		jaSites?.data?.jiraAlignAgg_sitesByOrgId
			?.map((site) => parseAriSafely(site.id)?.resourceId)
			.filter((id): id is string => Boolean(id)) ?? [];

	if (!jaInstanceCloudIds.includes(ariCloudId)) {
		return { status: 'notFound' };
	}

	return { status: 'inWorkspace' };
};
